import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, PhoneCall, GraduationCap, BrainCircuit, Briefcase, Cloud, Code2, FolderKanban, HeartPulse, LineChart, Megaphone, Network, ShieldCheck } from "lucide-react";

const CAT_ICONS = { GraduationCap, BrainCircuit, Briefcase, Cloud, Code2, FolderKanban, HeartPulse, LineChart, Megaphone, Network, ShieldCheck };
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import CallbackModal from "@/components/CallbackModal";
import DomainTemplateRenderer from "@/components/admin/template/DomainTemplateRenderer";
import { useSEO, JsonLd } from "@/lib/seo";
import { DOMAIN_CITY_LINKS, DOMAIN_CANONICAL_PATH } from "@/lib/domainLinks";
import { trackEvent } from "@/lib/analytics";

export default function Domain({ fixedSlug = "", canonicalPath = "" }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const canonPath = canonicalPath || DOMAIN_CANONICAL_PATH[slug] || `/domains/${slug}`;
  const [cat, setCat] = useState(null);
  const [tplPage, setTplPage] = useState(null);
  const [callbackOpen, setCallbackOpen] = useState(false);

  useEffect(() => {
    api.get(`/categories/${slug}`).then((r) => { setCat(r.data); trackEvent("page_view", { page: `domain:${slug}` }); }).catch(() => setCat(false));
    api.get(`/domain-page/${slug}`).then((r) => setTplPage(r.data)).catch(() => setTplPage({ template: null }));
  }, [slug]);

  useSEO(cat ? {
    title: `${cat.name} Courses — Training Basket`,
    description: cat.meta_description || cat.description,
    canonical: `https://www.trainingbasket.in${canonPath}`,
    image: cat.image,
  } : {});

  if (cat === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="font-bold text-xl">Domain not found</p><Link to="/courses" className="text-rose-600 font-semibold text-sm mt-2 inline-block">Browse all programs</Link></div>;
  if (!cat) return <div className="max-w-7xl mx-auto px-6 py-16"><div className="h-96 rounded-3xl bg-slate-100 animate-pulse" /></div>;

  const Icon = CAT_ICONS[cat.icon] || GraduationCap;

  if (tplPage?.template && (tplPage.sections || []).length) {
    return (
      <div data-testid="domain-page-templated">
        <JsonLd data={{
          "@context": "https://schema.org", "@type": "CollectionPage",
          name: `${cat.name} Courses — Training Basket`, description: cat.description,
          url: `${window.location.origin}/domains/${slug}`,
        }} />
        <DomainTemplateRenderer sections={tplPage.sections} category={cat} programs={tplPage.programs || []} onInquiry={() => setCallbackOpen(true)} />
        <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} />
      </div>
    );
  }

  return (
    <div data-testid="domain-page">
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "CollectionPage",
        name: `${cat.name} Courses — Training Basket`, description: cat.description,
        url: `${window.location.origin}/domains/${slug}`,
      }} />
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-rose-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <Link to="/courses" className="text-slate-300 text-sm flex items-center gap-1.5 mb-6 hover:text-white" data-testid="back-to-catalog"><ArrowLeft className="w-4 h-4" /> All programs</Link>
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-rose-600 flex items-center justify-center shrink-0"><Icon className="w-7 h-7" /></span>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{cat.name}</h1>
              <p className="text-rose-300 font-semibold mt-1">{cat.tagline}</p>
            </div>
          </div>
          <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">{cat.description}</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {(cat.skills || []).map((s) => (
              <Link key={s} to={`/courses?category=${slug}&skill=${encodeURIComponent(s)}`} data-testid={`domain-skill-${s.replace(/\s+/g, "-")}`}
                className="text-xs font-semibold px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors">{s}</Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button className="bg-rose-600 hover:bg-rose-700 rounded-xl font-bold py-6 px-7" onClick={() => setCallbackOpen(true)} data-testid="domain-counselor-cta">
              <PhoneCall className="w-4 h-4 mr-1.5" /> Get free counseling
            </Button>
            <p className="text-slate-400 text-sm self-center">{cat.programs.length} program{cat.programs.length === 1 ? "" : "s"} in this domain</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cat.programs.map((p) => <CourseCard key={p.slug} program={p} />)}
        </div>
        {cat.programs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-14 text-center bg-white">
            <p className="font-semibold text-slate-700">Programs for this domain are being finalized</p>
            <p className="text-sm text-slate-500 mt-1">Request a callback and we'll notify you first.</p>
          </div>
        )}
      </section>

      {(DOMAIN_CITY_LINKS[slug] || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12" data-testid="domain-city-links">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Popular {cat.name} course pages</h2>
          <div className="flex flex-wrap gap-2.5">
            {DOMAIN_CITY_LINKS[slug].map(([label, url]) => (
              <Link key={url} to={url} data-testid={`domain-city-${url.slice(1).replace(/\//g, "-")}`}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 transition-colors">{label}</Link>
            ))}
          </div>
        </section>
      )}

      {(cat.faqs || []).length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 text-center mb-8">FAQs — {cat.name}</h2>
          <Accordion type="single" collapsible data-testid="domain-faq">
            {cat.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`domain-faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-800">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} />
    </div>
  );
}
